// Captures UTM parameters and attribution data on first page load,
// persists them in sessionStorage so they survive navigation within the session,
// and exposes a helper to read them when submitting forms.

const STORAGE_KEY = "glp_attribution_v1";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_page?: string;
};

function readStored(): Attribution {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : {};
  } catch {
    return {};
  }
}

function writeStored(data: Attribution) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore storage errors (private mode, etc.)
  }
}

/**
 * Reads UTM params from the current URL and stores them.
 * Uses a last-touch strategy: whenever a navigation brings new UTMs in the
 * query string, they replace the stored ones so the form always submits the
 * most relevant attribution. When no UTMs are present, the previously stored
 * values are preserved. Safe to call on every route change.
 */
export function captureAttribution() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const incoming: Attribution = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) incoming[key] = v.slice(0, 200);
  }

  const stored = readStored();
  const hasIncomingUtms = Object.keys(incoming).length > 0;

  // Last touch wins: new UTMs on the URL replace the stored set; otherwise
  // we keep whatever was previously captured.
  const next: Attribution = hasIncomingUtms ? { ...incoming } : { ...stored };

  if (!stored.landing_page) {
    next.landing_page = window.location.href.slice(0, 500);
  } else {
    next.landing_page = stored.landing_page;
  }
  if (!stored.referrer) {
    next.referrer = document.referrer ? document.referrer.slice(0, 500) : stored.referrer;
  } else {
    next.referrer = stored.referrer;
  }

  writeStored(next);
}

export function getAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  return readStored();
}